import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useSnapshot } from 'valtio';

import type { AssetListItem, AssetType } from '@cartwhl/common/api/getAssets';
import Text from '@cartwhl/ui/base/TextNew';

import { userStore } from 'stores/global';

import { isDisplayableAssetCreator } from '../../AssetLibrary/assetLibraryFilters';
import AssetLibraryCreatorAvatar from '../../AssetLibrary/components/AssetLibraryCreatorAvatar';
import AssetLibraryStackedThumbnail from '../../AssetLibrary/components/AssetLibraryStackedThumbnail';
import AssetLibraryThumbnail from '../../AssetLibrary/components/AssetLibraryThumbnail';
import { useCharacterBatchHistoryPreviewURL } from '../../BatchCharacter/characterThumbnails';
import { CharacterBatchHistoryJobThumbnail } from '../../shared/BatchHistoryJobThumbnail';
import { isTeamPlan } from '../../AssetLibrary/utils';
import comicPreviewSrc from '../assets/Comic.svg';
import mascotPreviewSrc from '../assets/Mascot.svg';
import swingPreviewSrc from '../assets/Swing.svg';
import type { DashboardWorkItem, DashboardWorkItemKind } from '../types';
import { formatEditedAt } from '../workItemGrouping';
import DashboardFolderPreview from './DashboardFolderPreview';

export interface DashboardWorkCardProps {
  item: DashboardWorkItem;
}

const STACK_PEEK_WIDTH_PX = 16;
const CREATOR_AVATAR_SIZE = 5;
const CREATOR_AVATAR_INDENT_CLASS = 'pl-7';

export default function DashboardWorkCard({ item }: DashboardWorkCardProps) {
  const { organizationInfo, organizationMembers, apiKey } = useSnapshot(
    userStore.state,
  );
  const characterBatchPreviewURL = useCharacterBatchHistoryPreviewURL(
    item.kind === 'character-job' ? item.id : undefined,
    item.batchStatus,
    apiKey.data?.apiKey,
  );
  const [isHovered, setIsHovered] = useState(false);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const showStackedPreview =
    item.kind !== 'character-job' && shouldShowStackedPreview(item);
  const showCreatorAvatar = isTeamPlan(organizationInfo?.planType);
  const metadataIndentClass = showCreatorAvatar
    ? CREATOR_AVATAR_INDENT_CLASS
    : undefined;
  const thumbnailSrc = item.thumbnailSrc?.trim() || '';
  const hasUsableThumbnail = Boolean(thumbnailSrc) && !thumbnailFailed;
  const folderPreviewSrc = getFolderPreviewImageSrc(item);
  const thumbnailAsset = toDashboardThumbnailAsset(item);
  const metadata = item.itemCount
    ? `${item.kindLabel} — ${item.itemCount} ${item.itemCount === 1 ? 'item' : 'items'}`
    : item.kindLabel;

  useEffect(() => {
    setThumbnailFailed(false);
  }, [item.thumbnailSrc]);

  const content = (
    <>
      <div
        className="bg-tertiary relative aspect-video w-full overflow-hidden rounded-lg"
        style={
          showStackedPreview && hasUsableThumbnail
            ? { paddingRight: STACK_PEEK_WIDTH_PX }
            : undefined
        }
      >
        {item.kind === 'character-job' ? (
          <CharacterBatchHistoryJobThumbnail
            batchID={item.id}
            status={item.batchStatus ?? 'QUEUED'}
            numTotalJobs={item.itemCount ?? 1}
            resolvedPreviewURL={characterBatchPreviewURL}
          />
        ) : hasUsableThumbnail && showStackedPreview ? (
          <AssetLibraryStackedThumbnail
            asset={thumbnailAsset}
            isHovered={isHovered}
            onLoadError={() => setThumbnailFailed(true)}
          />
        ) : hasUsableThumbnail ? (
          <AssetLibraryThumbnail
            assetType={thumbnailAsset.assetType}
            thumbnailURL={thumbnailAsset.thumbnailURL}
            isHovered={isHovered}
            onLoadError={() => setThumbnailFailed(true)}
          />
        ) : folderPreviewSrc ? (
          <DashboardFolderPreview
            label={getPreviewLabel(item)}
            imageSrc={folderPreviewSrc}
          />
        ) : item.kind === 'scene' || item.kind === 'canvas-project' ? (
          <AssetLibraryThumbnail
            assetType={thumbnailAsset.assetType}
            thumbnailURL={null}
          />
        ) : (
          <DashboardFolderPreview label={getPreviewLabel(item)} />
        )}
      </div>
      <div className="flex min-w-0 flex-col gap-1 px-2">
        <div className="flex min-w-0 items-center gap-2">
          {showCreatorAvatar ? (
            isDisplayableAssetCreator(item.createdByUser) ? (
              <AssetLibraryCreatorAvatar
                size={CREATOR_AVATAR_SIZE}
                className="shrink-0"
                userID={item.createdByUser}
                organizationMembers={organizationMembers}
              />
            ) : (
              <div
                className="bg-gray-30 flex shrink-0 items-center justify-center rounded-full"
                style={{
                  width: `${CREATOR_AVATAR_SIZE / 4}rem`,
                  height: `${CREATOR_AVATAR_SIZE / 4}rem`,
                }}
                aria-label="Unknown creator"
              >
                <Text token="14/medium" className="text-secondary leading-none">
                  ?
                </Text>
              </div>
            )
          ) : null}
          <Text
            token="16/medium"
            className="text-secondary min-w-0 truncate"
            title={item.title}
          >
            {item.title}
          </Text>
        </div>
        <Text
          token="14"
          className={`text-tertiary truncate ${metadataIndentClass ?? ''}`}
          title={metadata}
        >
          {metadata}
        </Text>
        <Text
          token="14"
          className={`text-tertiary ${metadataIndentClass ?? ''}`}
        >
          Edited {formatEditedAt(item.updatedAt)}
        </Text>
      </div>
    </>
  );
  const className =
    'flex min-w-0 flex-col gap-4 rounded-2xl p-2 pb-4 transition-colors hover:bg-secondary';

  if (item.external) {
    return (
      <a
        href={item.href}
        className={className}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      to={item.href}
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {content}
    </Link>
  );
}

function shouldShowStackedPreview(item: DashboardWorkItem): boolean {
  return (
    (item.kind === 'comic-job' ||
      item.kind === 'swing-job' ||
      item.kind === 'mascot-job') &&
    (item.itemCount ?? 0) > 1
  );
}

function getPreviewLabel(item: DashboardWorkItem) {
  if (item.kind === 'canvas-project') return 'Canvas';
  if (item.kind === 'comic-job') return 'Performance Capture';
  if (item.kind === 'swing-job') return 'Performance Generation';
  if (item.kind === 'mascot-job') return 'Mascot';
  if (item.kind === 'character-job') return 'Character Generator';
  return 'Scene';
}

function getFolderPreviewImageSrc(item: DashboardWorkItem): string | undefined {
  if (item.kind === 'comic-job') return comicPreviewSrc;
  if (item.kind === 'swing-job') return swingPreviewSrc;
  if (item.kind === 'mascot-job') return mascotPreviewSrc;
  return undefined;
}

function toDashboardThumbnailAssetType(kind: DashboardWorkItemKind): AssetType {
  switch (kind) {
    case 'mascot-job':
      return 'MASCOT_JOB';
    case 'character-job':
      return 'MOGEN_JOB';
    case 'comic-job':
    case 'swing-job':
      return 'MOGEN_JOB';
    case 'scene':
    case 'canvas-project':
    default:
      return 'SCENE';
  }
}

function toDashboardThumbnailAsset(item: DashboardWorkItem): AssetListItem {
  return {
    assetID: item.id,
    assetName: item.title,
    assetType: toDashboardThumbnailAssetType(item.kind),
    createdAt: item.createdAt,
    createdBy: item.createdByUser ?? '',
    createdByUser: item.createdByUser ?? '',
    lastUpdatedAt: item.updatedAt,
    thumbnailURL: item.thumbnailSrc?.trim() || null,
  };
}
